import { PermissionCode, type MasterDataImportPreview } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiRequestError } from '@/lib/api';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { hasPermission } from '@/platform/auth/permissions';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import {
  useCommitMasterDataImport,
  useExportMasterData,
  usePreviewMasterDataImport,
} from '@/platform/features/master-data/use-master-data';

const STATUS_LABEL = { new: 'neu', existing: 'vorhanden' } as const;

export function MasterDataExchangePage() {
  const copy = useVoiceCopy();
  const me = useMe();
  const canImport = hasPermission(me.data, PermissionCode.MASTER_DATA_IMPORT);
  const canExport = hasPermission(me.data, PermissionCode.MASTER_DATA_EXPORT);
  const previewImport = usePreviewMasterDataImport();
  const commitImport = useCommitMasterDataImport();
  const exportFile = useExportMasterData();
  const [file, setFile] = useState<File | null>(null);
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<MasterDataImportPreview | null>(null);
  const [inputKey, setInputKey] = useState(0);

  async function showPreview() {
    if (!file) {
      toast.error(copy('platform.form.csv'));
      return;
    }
    const text = await readCsvFile(file);
    setCsv(text);
    try {
      setPreview(await previewImport.mutateAsync(text));
    } catch (error) {
      setPreview(null);
      toastFailure(error, 'Die Vorschau ist fehlgeschlagen.');
    }
  }

  async function confirm() {
    try {
      const result = await commitImport.mutateAsync(csv);
      const created = result.items.filter((item) => item.created).length;
      toast.success(
        `${created} neue Partner übernommen. Vorhandene Schlüssel bleiben unverändert.`,
      );
      setPreview(null);
      setFile(null);
      setCsv('');
      setInputKey((value) => value + 1);
    } catch (error) {
      toastFailure(error, 'Die Übernahme ist fehlgeschlagen.');
    }
  }

  async function download() {
    try {
      const fileResult = await exportFile.mutateAsync();
      const blob = new Blob([fileResult.csv], { type: fileResult.mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileResult.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastFailure(error, 'Der Export ist fehlgeschlagen.');
    }
  }

  const blocked = !preview || preview.errors.length > 0 || preview.rows.length === 0;

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Import und Export"
        description="Die Vorschau schreibt nichts. Die Übernahme gilt für die ganze Datei. Ein vorhandener externer Schlüssel legt keinen zweiten Partner an."
      />
      <p className="max-w-3xl text-sm text-muted-foreground">
        Pflichtspalten sind externalKey, legalName und validFrom. Weitere Spalten sind tradeName,
        partnerNumber, iban, accountHolder, currencyCode, bic, bankName, taxCountry, vatId und
        taxId. Trennzeichen ist das Semikolon oder das Komma.
      </p>
      {canExport ? (
        <Button
          type="button"
          variant="outline"
          disabled={exportFile.isPending}
          onClick={() => void download()}
        >
          Exportieren
        </Button>
      ) : null}
      {canImport ? (
        <div className="max-w-3xl space-y-4">
          <div className="space-y-2">
            <Label htmlFor="master-data-csv">Datei</Label>
            <Input
              key={inputKey}
              id="master-data-csv"
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setPreview(null);
                setCsv('');
              }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={previewImport.isPending}
              onClick={() => void showPreview()}
            >
              Vorschau
            </Button>
            <Button
              type="button"
              disabled={blocked || commitImport.isPending}
              onClick={() => void confirm()}
            >
              Übernehmen
            </Button>
          </div>
          {preview && preview.errors.length > 0 ? (
            <div role="alert" className="space-y-2 text-sm">
              <p>Die Datei enthält fehlerhafte Zeilen.</p>
              <ul className="list-disc space-y-1 pl-5">
                {preview.errors.map((error) => (
                  <li key={`${error.line}-${error.message}`}>
                    Zeile {error.line}: {error.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {preview && preview.rows.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zeile</TableHead>
                  <TableHead>Schlüssel</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Nummer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((row) => (
                  <TableRow key={`${row.line}-${row.externalKey}`}>
                    <TableCell>{row.line}</TableCell>
                    <TableCell className="font-mono text-sm">{row.externalKey}</TableCell>
                    <TableCell>{row.legalName}</TableCell>
                    <TableCell>{STATUS_LABEL[row.status]}</TableCell>
                    <TableCell>{row.partnerNumber ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Für den Import fehlt das Recht.</p>
      )}
    </section>
  );
}

function toastFailure(error: unknown, fallback: string) {
  toast.error(error instanceof ApiRequestError ? error.message : fallback);
}

function readCsvFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('read'));
    reader.readAsText(file);
  });
}
